import React, { useState, useEffect, useContext } from 'react';
import { ExternalLink, X } from 'lucide-react';
import axios from 'axios';
import { AuthContext } from '../App';

const PromotionalBanner = () => {
  const { API, user } = useContext(AuthContext);
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (user) {
      fetchBanners();
    }
  }, [user]);

  const fetchBanners = async () => {
    try {
      const token = localStorage.getItem('helply_token');
      const res = await axios.get(`${API}/ads/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(res.data.banners || []);
    } catch (error) {
      // Silent fail - banners are optional
    } finally {
      setLoaded(true);
    }
  };

  const trackClick = (bannerId) => {
    try {
      const token = localStorage.getItem('helply_token');
      axios.post(`${API}/ads/${bannerId}/click`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    } catch {}
  };

  const handleDismiss = (bannerId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDismissed(prev => [...prev, bannerId]);
  };

  const visibleBanners = banners.filter(b => !dismissed.includes(b.id));

  // Fallback to static GKV banner if no dynamic banners
  if (loaded && visibleBanners.length === 0) {
    return (
      <div className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-2 px-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
          <span className="text-sm font-medium">
            Professional consulting services - Visit GKV Consulting
          </span>
          <a
            href="https://www.gkv-consulting.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-semibold hover:underline"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="hidden sm:inline">www.gkv-consulting.com</span>
          </a>
        </div>
      </div>
    );
  }

  if (!loaded || visibleBanners.length === 0) return null;

  return (
    <div className="space-y-0">
      {visibleBanners.map((banner) => (
        banner.link_url ? (
          <a
            key={banner.id}
            href={banner.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block py-2.5 px-4 shadow-sm cursor-pointer hover:opacity-95 transition-opacity"
            style={{ backgroundColor: banner.bg_color || '#2563eb', color: banner.text_color || '#ffffff' }}
            onClick={() => trackClick(banner.id)}
            data-testid={`promo-banner-${banner.id}`}
          >
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {banner.image_url && (
                  <img
                    src={banner.image_url}
                    alt=""
                    className="w-7 h-7 rounded object-cover flex-shrink-0"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                <div className="min-w-0">
                  <span className="font-semibold text-sm">{banner.title}</span>
                  <span className="text-sm opacity-90 ml-2 hidden sm:inline">{banner.description}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="hidden sm:inline text-sm font-medium underline opacity-90 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  Learn more
                </span>
                <button
                  onClick={(e) => handleDismiss(banner.id, e)}
                  className="opacity-70 hover:opacity-100 transition-opacity p-0.5"
                  aria-label="Dismiss banner"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </a>
        ) : (
          <div
            key={banner.id}
            className="py-2.5 px-4 shadow-sm"
            style={{ backgroundColor: banner.bg_color || '#2563eb', color: banner.text_color || '#ffffff' }}
            data-testid={`promo-banner-${banner.id}`}
          >
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {banner.image_url && (
                  <img
                    src={banner.image_url}
                    alt=""
                    className="w-7 h-7 rounded object-cover flex-shrink-0"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                <div className="min-w-0">
                  <span className="font-semibold text-sm">{banner.title}</span>
                  <span className="text-sm opacity-90 ml-2 hidden sm:inline">{banner.description}</span>
                </div>
              </div>
              <button
                onClick={(e) => handleDismiss(banner.id, e)}
                className="opacity-70 hover:opacity-100 transition-opacity p-0.5 flex-shrink-0"
                aria-label="Dismiss banner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )
      ))}
    </div>
  );
};

export default PromotionalBanner;
